import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { progressService } from '@/services/progressService'
import { useSessionStore } from './sessionStore'

const AUTO_FLUSH_INTERVAL = 5 * 60 * 1000

export interface LessonProgressData {
  completionPercentage: number
  timeSpent: number
  status: 'not_started' | 'in_progress' | 'completed'
  score?: number
  lastAccessedAt: string
}

export interface LessonProgressCache {
  [lessonId: number]: LessonProgressData
}

interface LessonProgressState {
  progressCache: LessonProgressCache
  loading: boolean
  error: string | null
  pendingUpdates: Map<number, { timeout: ReturnType<typeof setTimeout>; resolve: () => void }>
  userId: number | null
  dirtyEnrollmentIds: Set<number>
  _flushTimer: number | null
  _isFlushing: boolean

  setUserId: (userId: number) => void
  markEnrollmentsDirty: (ids: number[]) => void
  flushEnrollments: () => Promise<void>
  startAutoFlush: () => void
  stopAutoFlush: () => void
  getProgress: (lessonId: number) => LessonProgressData | undefined
  loadProgress: (lessonId: number) => Promise<LessonProgressData>
  updateProgress: (lessonId: number, data: Partial<LessonProgressData>, debounceMs?: number) => Promise<void>
  markCompleted: (lessonId: number) => Promise<void>
  clearCache: () => void
  clearPendingUpdates: () => void
}

const getInitialProgress = (): LessonProgressData => ({
  completionPercentage: 0,
  timeSpent: 0,
  status: 'not_started',
  lastAccessedAt: new Date().toISOString()
})

export const useLessonProgressStore = create<LessonProgressState>()(
  persist(
    (set, get) => ({
      progressCache: {},
      loading: false,
      error: null,
      pendingUpdates: new Map<number, { timeout: ReturnType<typeof setTimeout>; resolve: () => void }>(),
      userId: null,
      dirtyEnrollmentIds: new Set<number>(),
      _flushTimer: null,
      _isFlushing: false,

      setUserId: (userId: number) => set({ userId }),

      markEnrollmentsDirty: (ids) => {
        if (ids.length === 0) return
        set((prev) => {
          const next = new Set(prev.dirtyEnrollmentIds)
          ids.forEach((id) => next.add(id))
          return { dirtyEnrollmentIds: next }
        })
      },

      flushEnrollments: async () => {
        const { dirtyEnrollmentIds, userId, _isFlushing } = get()

        if (_isFlushing || dirtyEnrollmentIds.size === 0 || !userId) return

        const flushed = Array.from(dirtyEnrollmentIds)
        set({ _isFlushing: true })

        try {
          await progressService.updateEnrollments(userId, flushed)
          set((prev) => {
            const next = new Set(prev.dirtyEnrollmentIds)
            flushed.forEach((id) => next.delete(id))
            return { dirtyEnrollmentIds: next }
          })
        } catch (error) {
          console.error('Failed to flush enrollment progress:', error)
        } finally {
          set({ _isFlushing: false })
        }
      },

      startAutoFlush: () => {
        get().stopAutoFlush()

        const timer = window.setInterval(() => {
          if (get().dirtyEnrollmentIds.size > 0) {
            get().flushEnrollments()
          }
        }, AUTO_FLUSH_INTERVAL)

        set({ _flushTimer: timer })
      },

      stopAutoFlush: () => {
        const { _flushTimer } = get()
        if (_flushTimer !== null) {
          clearInterval(_flushTimer)
          set({ _flushTimer: null })
        }
      },

      getProgress: (lessonId: number) => {
        const { progressCache } = get()
        return progressCache[lessonId]
      },

      loadProgress: async (lessonId: number) => {
        const { userId, progressCache } = get()
        
        if (!userId) {
          console.error('No user ID set for loading progress')
          return getInitialProgress()
        }

        const cached = progressCache[lessonId]
        if (cached) {
          return cached
        }

        set({ loading: true, error: null })

        try {
          const progress = await progressService.calculateLessonProgress(userId, lessonId)
          
          const progressData: LessonProgressData = {
            completionPercentage: progress.completionPercentage,
            timeSpent: progress.timeSpent,
            status: progress.status,
            score: progress.score,
            lastAccessedAt: new Date().toISOString()
          }

          set((prev) => ({
            progressCache: {
              ...prev.progressCache,
              [lessonId]: progressData
            },
            loading: false
          }))

          return progressData
        } catch (error) {
          console.error('Failed to load progress:', error)
          set({ error: 'İlerleme yüklenemedi', loading: false })
          return getInitialProgress()
        }
      },

      updateProgress: (lessonId: number, data: Partial<LessonProgressData>, debounceMs = 3000) => {
        const { pendingUpdates, userId, progressCache } = get()

        if (!userId) {
          console.error('No user ID set for updating progress')
          return Promise.resolve()
        }

        const current = progressCache[lessonId] || getInitialProgress()
        const currentStatus = current.status

        const existing = pendingUpdates.get(lessonId)
        if (existing) {
          clearTimeout(existing.timeout)
          existing.resolve()
        }

        return new Promise<void>((resolve) => {
          const timeout = setTimeout(async () => {
            const { progressCache } = get()
            const currentNow = progressCache[lessonId] || getInitialProgress()

            const updated: LessonProgressData = {
              ...currentNow,
              ...data,
              lastAccessedAt: new Date().toISOString()
            }

            const durationSeconds = useSessionStore.getState().durationSeconds
            const durationMinutes = Math.floor(durationSeconds / 60)
            const newStatus = updated.completionPercentage >= 100
              ? 'completed'
              : (updated.completionPercentage > 0 ? 'in_progress' : 'not_started')

            const statusChanged = currentStatus !== newStatus

            try {
              await progressService.updateProgress(userId, lessonId, {
                completionPercentage: updated.completionPercentage,
                timeSpent: durationMinutes,
                status: newStatus,
                score: updated.score
              })

              const affectedEnrollmentIds = await progressService.getAffectedEnrollmentIds(userId, lessonId)

              if (affectedEnrollmentIds.length > 0) {
                get().markEnrollmentsDirty(affectedEnrollmentIds)

                if (statusChanged && newStatus === 'completed') {
                  await get().flushEnrollments()
                }
              }

              set((prev) => ({
                progressCache: {
                  ...prev.progressCache,
                  [lessonId]: updated
                },
                pendingUpdates: new Map(Array.from(prev.pendingUpdates.entries()).filter(([id]) => id !== lessonId))
              }))
            } catch (error) {
              console.error('Failed to update progress:', error)
              set({ error: 'İlerleme kaydedilemedi' })
            }

            resolve()
          }, debounceMs)

          set((prev) => ({
            pendingUpdates: new Map(prev.pendingUpdates).set(lessonId, { timeout, resolve })
          }))
        })
      },

      markCompleted: async (lessonId: number) => {
        const { progressCache, userId } = get()

        if (!userId) {
          console.error('No user ID set for marking completed')
          return
        }

        const current = progressCache[lessonId] || getInitialProgress()
        const durationSeconds = useSessionStore.getState().durationSeconds
        const durationMinutes = Math.floor(durationSeconds / 60)

        try {
          await progressService.updateProgress(userId, lessonId, {
            completionPercentage: 100,
            timeSpent: durationMinutes,
            status: 'completed'
          })

          await get().flushEnrollments()

          set((prev) => ({
            progressCache: {
              ...prev.progressCache,
              [lessonId]: {
                ...current,
                completionPercentage: 100,
                status: 'completed'
              }
            }
          }))
        } catch (error) {
          console.error('Failed to mark lesson as completed:', error)
          set({ error: 'İlerleme kaydedilemedi' })
        }
      },

      clearCache: () => set({ progressCache: {} }),

      clearPendingUpdates: () => {
        const { pendingUpdates } = get()
        pendingUpdates.forEach(({ timeout }) => clearTimeout(timeout))
        set({ pendingUpdates: new Map() })
      }
    }),
    {
      name: 'lesson-progress-storage',
      partialize: (state) => ({
        progressCache: state.progressCache
      }),
      onRehydrateStorage: () => (state) => {
        state.pendingUpdates = new Map<number, { timeout: ReturnType<typeof setTimeout>; resolve: () => void }>()
      }
    }
  )
)

export const useLessonProgress = (lessonId: number) => {
  return useLessonProgressStore(
    (state) => state.progressCache[lessonId]
  )
}

export const useIsLessonCompleted = (lessonId: number) => {
  return useLessonProgressStore(
    (state) => state.progressCache[lessonId]?.status === 'completed'
  )
}
